import { useEffect, useState } from "react"
import type { LibraryItem } from "@/components/library/library-item"
import { LibraryItems, LibraryItemsReporter } from "@/components/library/library-items"
import {
  LibraryNodeEmpty,
  LibraryNodeError,
  LibraryNodeLoading,
  type LibrarySlot,
} from "@/components/library/library-node-state"
import { useTranslation } from "@/hooks/use-translation"
import { formatMs } from "@/lib/format-ms"
import { frameFormatClasses } from "@/lib/frame-format"
import {
  fetchLibraryEpisodeAnimatic,
  type LibraryAnimaticSegmentRow,
  type LibraryEpisodeAnimatic,
} from "@/lib/library-api"
import { animaticSegmentSource } from "@/lib/library-source"
import type { LibraryViewMode } from "@/lib/library-view-mode"

type Props = {
  projectId: number
  episodeId: number
  frameFormat: string
  mode: LibraryViewMode
  focusedKey?: string | null
  onFocusItem?: (key: string) => void
  onOpenItem?: (item: LibraryItem) => void
  onItemsChange?: (items: LibraryItem[]) => void
}

const NO_ITEMS: LibraryItem[] = []

/**
 * Узел «Аниматик» эпизода: сегменты нарезки ленты. Сегмент — не отдельный файл,
 * а диапазон внутри файла-источника, поэтому плитка играет подрезанный кусок
 * исходника (sourceInMs/sourceOutMs), а постер режет фоновая джоба.
 */
export function LibraryAnimaticNode({
  projectId,
  episodeId,
  frameFormat,
  mode,
  focusedKey,
  onFocusItem,
  onOpenItem,
  onItemsChange,
}: Props) {
  const { t } = useTranslation(["workspace", "common"])
  const [slot, setSlot] = useState<LibrarySlot<LibraryEpisodeAnimatic | null>>({ status: "loading" })
  const [reloadKey, setReloadKey] = useState(0)
  const ff = frameFormatClasses(frameFormat)

  useEffect(() => {
    const controller = new AbortController()
    setSlot({ status: "loading" })
    fetchLibraryEpisodeAnimatic(projectId, episodeId, { signal: controller.signal })
      .then((animatic) => setSlot({ status: "ready", data: animatic }))
      .catch(() => {
        if (!controller.signal.aborted) setSlot({ status: "error" })
      })
    return () => controller.abort()
  }, [projectId, episodeId, reloadKey])

  if (slot.status === "loading" || slot.status === "error") {
    return (
      <>
        <LibraryItemsReporter items={NO_ITEMS} onItemsChange={onItemsChange} />
        {slot.status === "loading" ? (
          <LibraryNodeLoading />
        ) : (
          <LibraryNodeError onRetry={() => setReloadKey((k) => k + 1)} />
        )}
      </>
    )
  }

  const segments = slot.data?.segments ?? []

  const segmentItem = (seg: LibraryAnimaticSegmentRow): LibraryItem => {
    const label = t("library.animatic.segmentLabel", { number: seg.position + 1 })
    const range = `${formatMs(seg.global_start_ms)}–${formatMs(seg.global_end_ms)}`
    const shotNumber = seg.shot
      ? seg.shot.scene_number != null
        ? `${seg.shot.scene_number}.${seg.shot.number}`
        : String(seg.shot.number)
      : null
    const linkedTail = shotNumber ? t("library.animatic.linkedShot", { number: shotNumber }) : null
    return {
      key: `seg-${seg.id}`,
      kind: "video",
      pickMedia: "animatic",
      label,
      meta: linkedTail ? `${range} · ${linkedTail}` : range,
      thumbUrl: seg.poster_url,
      // Привязанный сегмент выбрать нельзя: гасит lockedReason, а свой шот узнаёт его по source.
      source: animaticSegmentSource(seg.id),
      lockedReason: shotNumber ? t("library.animatic.lockedToShot", { number: shotNumber }) : null,
      asset: seg.video_stream_url
        ? {
            title: linkedTail ? `${label} · ${linkedTail}` : label,
            kind: "video",
            videoUrl: seg.video_stream_url,
            durationMs: seg.source_duration_ms,
            frameRate: seg.frame_rate,
            aspectRatio: seg.aspect_ratio,
            sourceId: seg.id,
            sourceInMs: seg.start_ms,
            sourceOutMs: seg.end_ms,
          }
        : undefined,
    }
  }

  const items = segments.map(segmentItem)

  return (
    <>
      <LibraryItemsReporter items={items} onItemsChange={onItemsChange} />
      {items.length === 0 ? (
        <LibraryNodeEmpty>{t("library.animatic.empty")}</LibraryNodeEmpty>
      ) : (
        <LibraryItems
          items={items}
          mode={mode}
          listLayout="table"
          aspectClass={ff.aspect}
          focusedKey={focusedKey}
          onFocusItem={onFocusItem}
          onOpenItem={onOpenItem}
        />
      )}
    </>
  )
}
